import * as d3 from 'd3';

import { degreesToCardinalDirection } from '@/utils';

import { addCompassLabelsToRadialChart, addRadialSpokesToRadialChart, getSvgByContainerId } from './common';

export type RadialCurrentsChartDatum = { direction: number; speed: string; frequency: number };
type StackItem = [string, d3.InternMap<string, RadialCurrentsChartDatum>];

type RadialBarHistogramChartProps = {
    dataset: RadialCurrentsChartDatum[];
    title?: string;
    containerId: string;
};

const CHART_WIDTH = 540;
const CHART_HEIGHT = 500;
const GRID_STROKE_WIDTH = 1;
const OUTER_RING_STROKE_WIDTH = 2;
const INNER_RADIUS = 10;
const OUTER_RADIUS = 180;
const BARS_COUNT = 16;
export const DIRECTION_BUCKET_SIZE_DEGREES = 360 / BARS_COUNT; // 22.5 degrees
export const DIRECTION_BUCKET_CENTERS_DEGREES = Array.from(
    { length: BARS_COUNT },
    (_, i) => i * DIRECTION_BUCKET_SIZE_DEGREES
);

export const radialBarHistogramChart = ({ dataset, containerId }: RadialBarHistogramChartProps) => {
    if (!dataset || dataset?.length === 0) {
        return;
    }
    // remove the previous chart if it exists
    const previousSvg = getSvgByContainerId(containerId);
    if (previousSvg) {
        previousSvg.remove();
    }

    // Stack the dataset into series by speed
    const uniqueSpeedLabels = d3.union(dataset.map((d) => d.speed));
    const stack = d3.stack<StackItem, string>().keys(uniqueSpeedLabels);
    const series = stack.value(([_, datum], key) => datum.get(key)?.frequency ?? 0)(
        // get value for each series key and stack
        d3.index(
            dataset,
            (d) => `${d.direction}`,
            (d) => d.speed
        )
    ); // group by stack then series key

    // An angular x-scale
    const x = d3
        .scaleBand()
        .domain(DIRECTION_BUCKET_CENTERS_DEGREES.map((d) => `${d}`))
        /*
            This slight range offset is to make sure that the 0 bar (bucket between 354.375 and 5.625 degrees) is
            aligned with the 0 degrees on the chart instead of being positioned between 0 and 11.25 degrees
         */
        .range([-Math.PI / BARS_COUNT, 2 * Math.PI - Math.PI / BARS_COUNT]);
    // A linear y-scale
    const y = d3
        .scaleLinear()
        .domain([0, d3.max(series, (d) => d3.max(d, (d) => d[1])) ?? 0])
        .range([INNER_RADIUS, OUTER_RADIUS]);

    const arc = d3
        .arc<d3.SeriesPoint<StackItem>>()
        .innerRadius((d) => y(d[0]))
        .outerRadius((d) => y(d[1]))
        .startAngle((d) => x(d.data[0]) ?? 0)
        .endAngle((d) => (x(d.data[0]) ?? 0) + x.bandwidth())
        .padAngle(3 / INNER_RADIUS)
        .padRadius(INNER_RADIUS);

    const color = d3
        .scaleOrdinal<string>()
        .domain(
            series
                .map((d) => d.key)
                .toSorted((a, b) => {
                    // a,b here are lables like "1 - 12 cm/s", "13 - 24 cm/s", etc.
                    const minA = Number(a.split(' - ')[0]);
                    const minB = Number(b.split(' - ')[0]);
                    // sorting in descending order to make sure the smallest speed is on top of the legend and gets a colder color
                    return minB - minA;
                })
        )
        .range(d3.schemeSpectral[Math.max(series.length, 3)])
        .unknown('#ccc');

    // Create the SVG container
    const svg = d3
        .select(`#${CSS.escape(containerId)}`)
        .append('svg')
        .attr('width', CHART_WIDTH)
        .attr('height', CHART_HEIGHT)
        .attr('viewBox', [-OUTER_RADIUS - 20, -150, CHART_WIDTH, CHART_HEIGHT - 100]);

    // radial grid circles
    const nRings = 5;
    const radii = d3.range(1, nRings + 1).map(
        (i) =>
            // start at innerRadius, end at outerRadius
            INNER_RADIUS + (OUTER_RADIUS - INNER_RADIUS) * (i / nRings)
    );
    svg.append('g')
        .selectAll('circle')
        .data(radii)
        .join('circle')
        .attr('fill', 'none')
        .attr('stroke', 'gray')
        .attr('stroke-opacity', 0.6)
        .attr('r', (d) => d);

    // radial spokes in the center of each direction bar
    const radialSpokesGroup = svg.append('g');

    addRadialSpokesToRadialChart(radialSpokesGroup, OUTER_RADIUS, DIRECTION_BUCKET_SIZE_DEGREES);

    // A group for each series, and a rect for each element in the series
    svg.append('g')
        .selectAll()
        .data(series)
        .join('g')
        .attr('fill', (d) => color(d.key))
        .selectAll('path')
        .data((D) =>
            D.map((d) => {
                // SeriesPoint is the correct type for d, and here we assign an additional property to it
                (d as d3.SeriesPoint<StackItem> & { key: string }).key = D.key;
                return d;
            })
        )
        .join('path')
        .attr('class', `arc-path-${containerId}`)
        .attr('d', arc)
        .append('title')
        .text((d) => {
            const typedD = d as d3.SeriesPoint<StackItem> & { key: string };
            const key = typedD.key;
            const dataPoint = typedD.data[1].get(key);
            return `Currents with the direction of ${degreesToCardinalDirection(dataPoint?.direction ?? 0, {
                fullNames: true,
            })} (${dataPoint?.direction} degrees) and a speed of ${
                typedD.key
            } were recorded ${dataPoint?.frequency} time(s)`;
        });

    // compass labels
    const compassGroup = svg.append('g');
    addCompassLabelsToRadialChart(compassGroup, OUTER_RADIUS);

    /*
        Frequency scale bar
    */
    const scaleGroup = svg.append('g').attr('transform', `translate(0, ${OUTER_RADIUS + 30})`);
    scaleGroup
        .append('text')
        .attr('x', -70)
        .attr('y', 5)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', 'grey')
        .text('Observation frequency');
    // main scale line
    scaleGroup
        .append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', OUTER_RADIUS)
        .attr('y2', 0)
        .attr('stroke', 'grey')
        .attr('stroke-width', GRID_STROKE_WIDTH);

    // ticks and labels
    const frequencyLines = [0, ...radii];
    const ticks = y.ticks(nRings);
    for (let i = 0; i <= nRings; i++) {
        const freq = frequencyLines[i] ?? 0;
        const tickLabel = ticks[i] ?? '';
        // label frequency
        scaleGroup
            .append('text')
            .attr('x', freq - 4)
            .attr('y', 16)
            .style('font-size', '10px')
            .style('fill', 'grey')
            .text(tickLabel);
        // First tick is a circle
        if (i === 0) {
            scaleGroup
                .append('circle')
                .attr('x1', 0)
                .attr('y1', -5)
                .attr('x2', 0)
                .attr('y2', 5)
                .attr('fill', 'grey')
                .attr('r', 3);
        } else {
            const tickLine = scaleGroup
                .append('line')
                .attr('x1', freq)
                .attr('y1', -5)
                .attr('x2', freq)
                .attr('y2', 5)
                .attr('stroke', 'grey');

            if (i === nRings) {
                tickLine.attr('stroke-width', OUTER_RING_STROKE_WIDTH);
            } else {
                tickLine.attr('stroke-width', GRID_STROKE_WIDTH);
            }
        }
    }

    // color legend
    svg.append('g')
        .selectAll()
        .data(color.domain())
        .join('g')
        .attr('transform', (d, i, nodes) => `translate(${OUTER_RADIUS + 30},${(nodes.length / 2 - i - 1) * 22})`)
        .call((g) => g.append('rect').attr('width', 18).attr('height', 18).attr('fill', color))
        .call((g) =>
            g
                .append('text')
                .attr('x', 24)
                .attr('y', 9)
                .attr('dy', '0.35em')
                .text((d) => d)
        );

    // mouseover event
    svg.on('mouseover', (e) => {
        const target = e.target as SVGElement;
        if (target.classList.contains(`arc-path-${containerId}`)) {
            target.setAttribute('filter', 'brightness(0.85)');
        }
    });
    svg.on('mouseout', (e) => {
        const target = e.target as SVGElement;
        if (target.classList.contains(`arc-path-${containerId}`)) {
            target.removeAttribute('filter');
        }
    });
};
